import { useNavigate } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "./CartContext";
import { CartLine } from "./CartLine";
import { money } from "../useProducts";

export const BagButton = ({ className = "", testId = "nav-bag-button" }) => {
  const { count, setOpen } = useCart();
  return (
    <button type="button" data-testid={testId} aria-label={`Open bag, ${count} item${count === 1 ? "" : "s"}`} onClick={() => setOpen(true)} className={`relative h-9 w-9 rounded-full border border-[#e3e2dc] flex items-center justify-center text-[#0a0a0b] hover:bg-[#0a0a0b] hover:text-white hover:border-[#0a0a0b] transition-colors duration-300 ${className}`}>
      <ShoppingBag size={15} />
      {count > 0 && (
        <span data-testid={`${testId}-count`} className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff2a85] text-white font-mono-d text-[9px] flex items-center justify-center">{count}</span>
      )}
    </button>
  );
};

export const CartDrawer = () => {
  const { items, count, subtotal, open, setOpen } = useCart();
  const navigate = useNavigate();
  const go = () => { setOpen(false); navigate("/cart"); };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md bg-[#faf9f6] border-[#e3e2dc] text-[#0a0a0b] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e3e2dc] text-left">
          <SheetTitle className="font-display font-extrabold uppercase text-lg">Your bag <span data-testid="cart-drawer-count" className="font-mono-d text-[10px] tracking-[0.2em] text-[#5f5e5a] ml-2">{count} item{count === 1 ? "" : "s"}</span></SheetTitle>
          <SheetDescription className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a]">Drop 001 · Pre-order</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6">
          {items.length === 0 ? (
            <div data-testid="cart-drawer-empty" className="py-20 text-center">
              <ShoppingBag size={28} className="mx-auto text-[#c9c8c2]" />
              <p className="mt-4 font-display font-extrabold uppercase text-sm">Your bag is empty</p>
              <p className="mt-2 text-sm text-[#5f5e5a]">Add a piece from the lookbook to get started.</p>
              <button type="button" data-testid="cart-drawer-shop" onClick={() => { setOpen(false); navigate("/shop"); }} className="mt-6 h-11 px-6 rounded-full bg-[#0a0a0b] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] hover:bg-[#ff2a85] transition-colors">Shop the drop</button>
            </div>
          ) : (
            <ul>{items.map((i) => <CartLine key={`${i.lookupKey}|${i.size}|${i.color}`} item={i} compact />)}</ul>
          )}
        </div>
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-[#e3e2dc] bg-[#ffffff]">
            <div className="flex items-center justify-between">
              <span className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a]">Subtotal</span>
              <span data-testid="cart-drawer-subtotal" className="font-mono-d text-base">{money(subtotal)}</span>
            </div>
            <p className="mt-1 text-[11px] text-[#5f5e5a]">Delivery and coupons are applied at checkout.</p>
            <button type="button" data-testid="cart-drawer-checkout" onClick={go} className="mt-4 w-full h-12 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] transition-colors">View bag & checkout</button>
            <button type="button" data-testid="cart-drawer-continue" onClick={() => setOpen(false)} className="mt-2 w-full h-10 font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a] hover:text-[#0a0a0b]">Continue shopping</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
